import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Tag, 
  Button, 
  Space, 
  Descriptions,
  Avatar,
  Timeline,
  Alert,
  Divider,
  List,
  message,
  Modal,
  Form,
  Input
} from 'antd';
import { 
  ArrowLeftOutlined,
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  PhoneOutlined,
  MailOutlined,
  SafetyOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { updateAssignment } from '../../api';

const { Title, Text, Paragraph } = Typography;

const ComplianceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { complianceId } = useParams();
  const [complianceData, setComplianceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    // Get compliance data from location state or fetch by ID
    if (location.state?.issue) {
      setComplianceData(location.state.issue);
    } else {
      // If no state data, you would fetch by complianceId here
      // For now, redirect back to compliance pending
      navigate('/agency/compliance-pending');
    }
  }, [location.state, complianceId, navigate]);

  if (!complianceData) {
    return (
      <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text>Loading compliance details...</Text>
          </div>
        </Card>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      'compliance-in-progress': 'orange',
      'compliance-pending': 'red',
      'compliance-approved': 'green',
      'active': 'green',
      'ongoing': 'green'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'compliance-in-progress': 'Compliance In Progress',
      'compliance-pending': 'Compliance Pending',
      'compliance-approved': 'Compliance Approved',
      'active': 'Active - Ongoing',
      'ongoing': 'Ongoing'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const handleApproveCompliance = () => {
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async (values) => {
    setApproving(true);
    try {
      // Update assignment status using transition parameter
      const res = await updateAssignment(complianceData._id, { 
        transition: 'complete_compliance',
        notes: values.notes
      });

      // Update local state with the response data or assume success
      const updatedData = {
        ...complianceData,
        status: 'active',
        approvalNotes: values.notes,
        approvedAt: new Date().toISOString()
      };
      setComplianceData(updatedData);

      message.success('Compliance approved successfully! Assignment is now active.');
      setShowApprovalModal(false);
      form.resetFields();
    } catch (error) {
      console.error('Error approving compliance:', error);
      message.error('Failed to approve compliance. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleModalCancel = () => {
    setShowApprovalModal(false);
    form.resetFields();
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/agency/compliance-pending')}
          style={{ marginBottom: '16px' }}
        >
          Back to Compliance Pending
        </Button>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} style={{ margin: 0, color: '#006971' }}>
              Compliance Details
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              Assignment ID: {complianceData.id}
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {complianceData.status === 'compliance-in-progress' && (
              <Button 
                type="primary" 
                icon={<SafetyOutlined />}
                onClick={handleApproveCompliance}
                style={{ 
                  backgroundColor: '#52c41a', 
                  borderColor: '#52c41a',
                  borderRadius: '6px' 
                }}
                size="large"
              >
                Approve Compliance
              </Button>
            )}
            <Tag 
              color={getStatusColor(complianceData.status)} 
              style={{ fontSize: '14px', padding: '4px 12px' }}
            >
              {getStatusText(complianceData.status)}
            </Tag>
          </div>
        </div>
      </div>

      {/* Alert for compliance status */}
      {complianceData.status === 'compliance-in-progress' && (
        <Alert
          message="Compliance In Progress"
          description="This assignment is currently undergoing compliance verification. Please ensure all required documents and certifications are submitted."
          type="warning"
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: '24px', borderRadius: '8px' }}
          showIcon
        />
      )}
      
      {complianceData.status === 'active' && (
        <Alert
          message="Assignment Active"
          description="This assignment has been approved and is now active. The healthcare professional can begin their duties."
          type="success"
          icon={<CheckCircleOutlined />}
          style={{ marginBottom: '24px', borderRadius: '8px' }}
          showIcon
        />
      )}

      <Row gutter={[24, 24]}>
        {/* Assignment Information */}
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <FileTextOutlined />
                Assignment Information
              </Space>
            }
            style={{ marginBottom: '24px', borderRadius: '8px' }}
          >
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Job Title" span={2}>
                <Text strong>{complianceData.job?.title || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Job Code">
                {complianceData.job?.code || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Department">
                {complianceData.job?.department || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {complianceData.job?.category || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color="blue">{complianceData.job?.type || 'N/A'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Start Date">
                <Space>
                  <CalendarOutlined />
                  {formatDate(complianceData.startDate)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="End Date">
                <Space>
                  <CalendarOutlined />
                  {formatDate(complianceData.endDate)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Hours per Day">
                {complianceData.job?.hoursPerDay || 'N/A'} hours
              </Descriptions.Item>
              <Descriptions.Item label="Days per Week">
                {complianceData.job?.daysPerWeek || 'N/A'} days
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Location Information */}
          <Card 
            title={
              <Space>
                <EnvironmentOutlined />
                Location & Facility
              </Space>
            }
            style={{ marginBottom: '24px', borderRadius: '8px' }}
          >
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Address">
                <div>
                  <div>{complianceData.job?.address?.street || 'N/A'}</div>
                  <div>
                    {complianceData.job?.address?.city || 'N/A'}, {complianceData.job?.address?.state || 'N/A'} {complianceData.job?.address?.zipCode || ''}
                  </div>
                  <div>{complianceData.job?.address?.country || 'N/A'}</div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Manager">
                {complianceData.job?.managerName || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Unit">
                {complianceData.job?.unit || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Compensation */}
          <Card 
            title={
              <Space>
                <DollarOutlined />
                Compensation Details
              </Space>
            }
            style={{ marginBottom: '24px', borderRadius: '8px' }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={8}>
                <div style={{ textAlign: 'center', padding: '16px', background: '#f9f9f9', borderRadius: '8px' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#006971' }}>
                    {formatCurrency(complianceData.job?.salary?.base || 0)}
                  </div>
                  <Text type="secondary">Base Rate/hr</Text>
                </div>
              </Col>
              <Col xs={12} sm={8}>
                <div style={{ textAlign: 'center', padding: '16px', background: '#f9f9f9', borderRadius: '8px' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#006971' }}>
                    {formatCurrency(complianceData.job?.salary?.overtime || 0)}
                  </div>
                  <Text type="secondary">Overtime/hr</Text>
                </div>
              </Col>
              <Col xs={12} sm={8}>
                <div style={{ textAlign: 'center', padding: '16px', background: '#f9f9f9', borderRadius: '8px' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#006971' }}>
                    {formatCurrency(complianceData.job?.salary?.holiday || 0)}
                  </div>
                  <Text type="secondary">Holiday/hr</Text>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Requirements */}
          <Card 
            title={
              <Space>
                <CheckCircleOutlined />
                Requirements & Skills
              </Space>
            }
            style={{ borderRadius: '8px' }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Required Skills:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {complianceData.job?.requiredSkills?.length > 0 ? (
                      complianceData.job.requiredSkills.map((skill, index) => (
                        <Tag key={index} color="red" style={{ marginBottom: '4px' }}>
                          {skill}
                        </Tag>
                      ))
                    ) : (
                      <Text type="secondary">No specific requirements listed</Text>
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Preferred Skills:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {complianceData.job?.preferredSkills?.length > 0 ? (
                      complianceData.job.preferredSkills.map((skill, index) => (
                        <Tag key={index} color="blue" style={{ marginBottom: '4px' }}>
                          {skill}
                        </Tag>
                      ))
                    ) : (
                      <Text type="secondary">No preferred skills listed</Text>
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={24}>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Mandatory Documents:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {complianceData.job?.mandatoryDocuments?.length > 0 ? (
                      complianceData.job.mandatoryDocuments.map((doc, index) => (
                        <Tag key={index} color="orange" style={{ marginBottom: '4px' }}>
                          {doc}
                        </Tag>
                      ))
                    ) : (
                      <Text type="secondary">No mandatory documents specified</Text>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col xs={24} lg={8}>
          {/* Healthcare Professional Info */}
          <Card 
            title={
              <Space>
                <UserOutlined />
                Healthcare Professional
              </Space>
            }
            style={{ marginBottom: '24px', borderRadius: '8px' }}
          >
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <Avatar 
                size={80} 
                icon={<UserOutlined />} 
                style={{ backgroundColor: '#006971', marginBottom: '12px' }}
              />
              <div>
                <Text strong style={{ fontSize: '16px' }}>
                  Employee ID: {complianceData.employee}
                </Text>
              </div>
            </div>
            
            <Divider />
            
            <div style={{ marginBottom: '12px' }}>
              <Text strong>Assignment Status:</Text>
              <div style={{ marginTop: '4px' }}>
                <Tag color={getStatusColor(complianceData.status)}>
                  {getStatusText(complianceData.status)}
                </Tag>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <Text strong>Agency Rate:</Text>
              <div style={{ marginTop: '4px' }}>
                <Text>{formatCurrency(complianceData.agencyRate || 0)}/hr</Text>
              </div>
            </div>
          </Card>

          {/* Timeline */}
          <Card 
            title={
              <Space>
                <ClockCircleOutlined />
                Timeline
              </Space>
            }
            style={{ marginBottom: '24px', borderRadius: '8px' }}
          >
            <Timeline>
              <Timeline.Item 
                color="blue"
                dot={<CalendarOutlined />}
              >
                <div>
                  <Text strong>Assignment Created</Text>
                  <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                    {formatDate(complianceData.createdAt)}
                  </div>
                </div>
              </Timeline.Item>
              
              <Timeline.Item 
                color={complianceData.status === 'active' ? 'green' : 'orange'}
                dot={complianceData.status === 'active' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
              >
                <div>
                  <Text strong>
                    {complianceData.status === 'active' ? 'Compliance Approved' : 'Compliance In Progress'}
                  </Text>
                  <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                    {complianceData.status === 'active' 
                      ? complianceData.approvedAt 
                        ? formatDate(complianceData.approvedAt)
                        : 'Recently approved'
                      : 'Awaiting document verification'
                    }
                  </div>
                </div>
              </Timeline.Item>
              
              <Timeline.Item 
                color={complianceData.status === 'active' ? 'blue' : 'gray'}
                dot={<CalendarOutlined />}
              >
                <div>
                  <Text strong={complianceData.status === 'active'} type={complianceData.status === 'active' ? 'default' : 'secondary'}>
                    Assignment Start
                  </Text>
                  <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                    {formatDate(complianceData.startDate)}
                  </div>
                </div>
              </Timeline.Item>
            </Timeline>
          </Card>

          {/* Time Off */}
          {complianceData.timeOffs?.length > 0 && (
            <Card 
              title={
                <Space>
                  <CalendarOutlined />
                  Scheduled Time Off
                </Space>
              }
              style={{ borderRadius: '8px' }}
            >
              <List
                size="small"
                dataSource={complianceData.timeOffs}
                renderItem={(timeOff) => (
                  <List.Item>
                    <CalendarOutlined style={{ marginRight: '8px', color: '#666' }} />
                    {formatDate(timeOff)}
                  </List.Item>
                )}
              />
            </Card>
          )}
        </Col>
      </Row>

      {/* Approval Modal */}
      <Modal
        title={
          <Space>
            <SafetyOutlined style={{ color: '#52c41a' }} />
            Approve Compliance
          </Space>
        }
        open={showApprovalModal}
        onCancel={handleModalCancel}
        footer={null}
        width={600}
        centered
      >
        <div style={{ marginBottom: '20px' }}>
          <Alert
            message="Compliance Approval"
            description="You are about to approve this assignment's compliance. Once approved, the assignment status will change to 'Active' and the healthcare professional can begin their duties."
            type="info"
            showIcon
            style={{ marginBottom: '20px' }}
          />
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleApprovalSubmit}
        >
          <Form.Item
            label="Approval Notes (Optional)"
            name="notes"
            rules={[
              { max: 500, message: 'Notes cannot exceed 500 characters' }
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Add any notes about the compliance approval..."
              showCount
              maxLength={500}
            />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
            <Button onClick={handleModalCancel}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={approving}
              icon={approving ? <LoadingOutlined /> : <SafetyOutlined />}
              style={{ 
                backgroundColor: '#52c41a', 
                borderColor: '#52c41a' 
              }}
            >
              {approving ? 'Approving...' : 'Approve Compliance'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ComplianceDetails; 